/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShopperSearch } from '@/scapi';
import { createProductUrl, resolveDeepLinkVariantId, resolveProductUrlSegment } from '@/lib/product/product-utils';
import type { ProductTileCardProps } from '@/components/product-tile-card';

type ProductSearchHit = ShopperSearch.schemas['ProductSearchHit'];

/**
 * Product-record image fields (Widen CDN URLs). These are the source of truth for tile imagery —
 * NOT the internal SFCC image system (`imageGroups` / DIS).
 *
 * Field IDs are `pdpMainImage` / `pdpAltImage1`. SCAPI serializes product custom attributes with a
 * `c_` prefix in the response, and on a search hit they ride on `representedProduct` (the concrete
 * product), so the payload key is `representedProduct.c_pdpMainImage`.
 */
const MAIN_IMAGE_FIELD = 'pdpMainImage';
const ALT_IMAGE_FIELD = 'pdpAltImage1';
/** Brand display name — rendered as the tile eyebrow (same product-record field as the PDP eyebrow). */
const BRAND_FIELD = 'brandDisplayName';

/** Read a non-empty string property off an object whose custom attrs are loosely typed. */
function readString(source: unknown, key: string): string | undefined {
    const value = (source as Record<string, unknown> | null | undefined)?.[key];
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/**
 * Resolve a product-record image field to its Widen URL. Prefers `representedProduct` (where SCAPI
 * places the concrete product's custom attributes on a search hit), falling back to the hit itself.
 */
function productImageUrl(hit: ProductSearchHit, fieldId: string): string | undefined {
    const key = `c_${fieldId}`;
    return readString(hit.representedProduct, key) ?? readString(hit, key);
}

/**
 * Main image of the first product in a result set — the first image a shopper sees on a listing
 * that has no Page Designer banner above the grid, and therefore that listing's `og:image` when no
 * authored image outranks it (UCR-3023).
 *
 * Skips leading products that have no main image (the tile renders a placeholder for those, which
 * is not worth sharing) and returns `undefined` when none of them do.
 */
export function firstTileImageUrl(hits: ProductSearchHit[] | undefined): string | undefined {
    for (const hit of hits ?? []) {
        const url = productImageUrl(hit, MAIN_IMAGE_FIELD);
        if (url) return url;
    }

    return undefined;
}

/**
 * Map a product search hit to the presentational props the brand product tile needs. Image URLs
 * come from the product record (`pdpMainImage` / `pdpAltImage1`); when a product has no main image
 * the tile renders its neutral placeholder rather than falling back to an internal SFCC image. The
 * PDP link mirrors the legacy tile: for a multi-variant master it deep-links the default represented
 * variant as the URL's SKU path segment. See `resolveDeepLinkVariantId` for when that is skipped, and
 * `resolveProductUrlSegment` for why a standard product is linked by name rather than by SKU.
 */
export function toProductTileCardProps(hit: ProductSearchHit): ProductTileCardProps {
    return {
        title: hit.productName ?? '',
        href: createProductUrl(resolveProductUrlSegment(hit), null, 'color', resolveDeepLinkVariantId(hit)),
        mainImageUrl: productImageUrl(hit, MAIN_IMAGE_FIELD) ?? '',
        altImageUrl: productImageUrl(hit, ALT_IMAGE_FIELD),
        // Brand eyebrow rides on `representedProduct` (like the images), falling back to the hit.
        eyebrow: readString(hit.representedProduct, `c_${BRAND_FIELD}`) ?? readString(hit, `c_${BRAND_FIELD}`),
    };
}
